import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { ShareDocument } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications, ui, placeholders } from "@/strings";
import { fieldLabelClass, textareaClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { apiAxios } from "@/api/config";
import type { HighlightWithMetadata } from "../types/highlightNavigation";

interface EditHighlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  highlight: HighlightWithMetadata;
  assignmentId: string;
  documentId: string;
  onSuccess?: (updated: ShareDocument) => void;
}

interface UpdateHighlightPayload {
  explanation: string | null;
  suggestion: string | null;
}

export function EditHighlightModal({
  isOpen,
  onClose,
  highlight,
  assignmentId,
  documentId,
  onSuccess,
}: EditHighlightModalProps) {
  const [explanation, setExplanation] = useState(highlight.explanation || "");
  const [suggestion, setSuggestion] = useState(highlight.suggestion || "");
  const { show } = useNotification();

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdateHighlightPayload) => {
      const response = await apiAxios.patch<ShareDocument>(
        `/assignments/${assignmentId}/documents/${documentId}/highlights/${highlight.id}`,
        payload
      );
      return response.data;
    },
    onSuccess: (updated) => {
      show(notifications.generic.saveSuccess, "success");
      onSuccess?.(updated);
      onClose();
    },
    onError: (error: unknown) => {
      const message: string = error instanceof Error ? error.message : notifications.generic.saveError;
      show(message, "error");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateMutation.mutate({
      explanation: explanation.trim() || null,
      suggestion: suggestion.trim() || null,
    });
  };

  const handleReset = () => {
    setExplanation(highlight.explanation || "");
    setSuggestion(highlight.suggestion || "");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" aria-label="Edit Highlight">
      <ModalHeader title="Edit Highlight" onClose={onClose} />

      <ModalBody>
        <form onSubmit={handleSubmit} id="edit-highlight-form" className="flex flex-col gap-4">
          {/* Phrase preview */}
          <div>
            <label className={fieldLabelClass}>{ui.labels.highlightedText}</label>
            <div className="p-3 bg-surface-secondary border border-border rounded-lg">
              <p className="text-sm text-text-theme-secondary">
                {/* eslint-disable-next-line no-restricted-syntax */}
                <span aria-hidden="true">&ldquo;</span>
                {highlight.phrase}
                {/* eslint-disable-next-line no-restricted-syntax */}
                <span aria-hidden="true">&rdquo;</span>
              </p>
            </div>
          </div>

          {/* Explanation field */}
          <div>
            <label htmlFor="explanation" className={fieldLabelClass}>
              {ui.labels.explanation}
            </label>
            <textarea
              id="explanation"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              className={textareaClass}
              rows={4}
              maxLength={3000}
              placeholder={placeholders.highlights.explainHighlight}
            />
            <p className="text-xs text-text-theme-muted mt-1">
              {explanation.length}
              {/* eslint-disable-next-line no-restricted-syntax */}
              {" / "}
              3000 characters
            </p>
          </div>

          {/* Suggestion field */}
          <div>
            <label htmlFor="suggestion" className={fieldLabelClass}>
              {ui.labels.suggestionOptional}
            </label>
            <textarea
              id="suggestion"
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              className={textareaClass}
              rows={3}
              maxLength={3000}
              placeholder={placeholders.highlights.suggestImprovement}
            />
            <p className="text-xs text-text-theme-muted mt-1">
              {suggestion.length}
              {/* eslint-disable-next-line no-restricted-syntax */}
              {" / "}
              3000 characters
            </p>
          </div>
        </form>
      </ModalBody>

      <ModalFooter>
        <button type="button" onClick={handleReset} className={buttonVariants.secondary}>
          {ui.actions.reset}
        </button>
        <button type="button" onClick={onClose} className={buttonVariants.secondary}>
          {ui.actions.cancel}
        </button>
        <button
          type="submit"
          form="edit-highlight-form"
          disabled={updateMutation.isPending}
          className={buttonVariants.primary}
        >
          {updateMutation.isPending ? ui.actions.saving : ui.actions.save}
        </button>
      </ModalFooter>
    </Modal>
  );
}
